import React, { useState, useEffect } from 'react';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Form,
    FormGroup,
    Label,
    Input,
    FormFeedback,
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    UncontrolledTooltip
} from 'reactstrap';
import { useDropzone } from 'react-dropzone';
import classnames from 'classnames';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { APIClient } from '../../../helpers/api_helper';
import EndpointHelpModal from './EndpointHelpModal';

// Google Analytics tracking
import { trackEndpointStep, trackEndpointSubmit } from '../../../helpers/analytics';

interface CreateEndpointModalProps {
    isOpen: boolean;
    toggle: () => void;
    projectId?: string;
    envId?: number; // 1 = Sandbox, 2 = Production
    editData?: any; // Endpoint data for editing
    isEditMode?: boolean;
}

interface EndpointFormData {
    title: string;
    description: string;
    endpointSlug: string;
    sampleFile: File | null;
    sampleText: string;
    sampleImage: string; // Base64 image data
    prompt: string;
    jsonFormatData: string;
    status: number;
    sourceType: number;
}

interface FormErrors {
    title?: string;
    description?: string;
    endpointSlug?: string;
    prompt?: string;
    jsonFormatData?: string;
}

const CreateEndpointModal: React.FC<CreateEndpointModalProps> = ({ isOpen, toggle, projectId, envId = 1, editData, isEditMode = false }) => {
    // Helper function to get base URL based on environment
    const getBaseUrl = () => {
        // Both sandbox and production use the same API URL now
        return process.env.REACT_APP_INTEGR_API_URL || 'https://api.integr.dev';
    };

    const [activeTab, setActiveTab] = useState(1);
    const [passedSteps, setPassedSteps] = useState([1]);
    const [isExecuting, setIsExecuting] = useState(false);
    const [executionOutput, setExecutionOutput] = useState('');
    const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
    const [formData, setFormData] = useState<EndpointFormData>({
        title: '',
        description: '',
        endpointSlug: '',
        sampleFile: null,
        sampleText: '',
        sampleImage: '',
        prompt: '',
        jsonFormatData: '',
        status: 1,
        sourceType: 1
    });
    const [errors, setErrors] = useState<FormErrors>({});
    const [isModalInitialized, setIsModalInitialized] = useState(false);

    // Effect to populate form data when modal opens
    useEffect(() => {
        // Only run when modal opens (isOpen transitions from false to true)
        if (isOpen && !isModalInitialized) {
            console.log('Modal opened - isEditMode:', isEditMode, 'editData:', editData);

            if (isEditMode && editData) {
                console.log('Edit mode - populating form with editData:', editData);
                setFormData({
                    title: editData.title || '',
                    description: editData.description || '',
                    endpointSlug: editData.endpointSlug || editData.endpointKey || '',
                    sampleFile: null, // File can't be pre-populated
                    sampleText: '',
                    sampleImage: '',
                    prompt: editData.prompt || '',
                    jsonFormatData: editData.jsonFormatData || editData.expectedJsonFormat || '',
                    status: editData.status !== undefined ? editData.status : 1,
                    sourceType: editData.sourceType !== undefined ? editData.sourceType : 1
                });
            } else if (!isEditMode) {
                // Reset form for create mode only when modal opens
                console.log('Create mode - initializing empty form');
                setFormData({
                    title: '',
                    description: '',
                    endpointSlug: '',
                    sampleFile: null,
                    sampleText: '',
                    sampleImage: '',
                    prompt: '',
                    jsonFormatData: '',
                    status: 1,
                    sourceType: 1
                });
            }
            setIsModalInitialized(true);
        }

        // Reset initialization flag when modal closes
        if (!isOpen && isModalInitialized) {
            setIsModalInitialized(false);
        }
    }, [isOpen, isEditMode, editData, isModalInitialized]);

    // Reset form and step when modal closes
    const handleToggle = () => {
        setActiveTab(1);
        setPassedSteps([1]);
        setFormData({
            title: '',
            description: '',
            endpointSlug: '',
            sampleFile: null,
            sampleText: '',
            sampleImage: '',
            prompt: '',
            jsonFormatData: '',
            status: 1,
            sourceType: 1
        });
        setErrors({});
        setExecutionOutput('');
        toggle();
    };

    // Step navigation using Arrow Nav pattern
    function toggleTab(tab: number) {
        if (activeTab !== tab) {
            const modifiedSteps = [...passedSteps, tab];

            if (tab >= 1 && tab <= 3) {
                setActiveTab(tab);
                setPassedSteps(modifiedSteps);

                // Track endpoint creation steps (only track forward progression, not edit mode)
                if (!isEditMode && tab > activeTab && tab >= 1 && tab <= 3) {
                    trackEndpointStep(tab as 1 | 2 | 3, formData.endpointSlug || undefined);
                }
            }
        }
    }

    // Validation functions
    const validateEndpointSlug = (slug: string) => {
        const regex = /^[a-z0-9-]{1,50}$/;
        return regex.test(slug);
    };

    const validateStep1 = () => {
        const newErrors: FormErrors = {};
        
        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        }
        
        if (!formData.description.trim()) {
            newErrors.description = 'Description is required';
        }
        
        if (!formData.endpointSlug.trim()) {
            newErrors.endpointSlug = 'Endpoint slug is required';
        } else if (!validateEndpointSlug(formData.endpointSlug)) {
            newErrors.endpointSlug = 'Endpoint slug must be lowercase letters, numbers, and dashes only';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateStep2 = () => {
        const newErrors: FormErrors = {};
        
        if (!formData.prompt.trim()) {
            newErrors.prompt = 'Prompt is required';
        }
        
        if (!formData.jsonFormatData.trim()) {
            newErrors.jsonFormatData = 'Expected JSON format is required';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Step navigation
    const handleNext = () => {
        if (activeTab === 1 && validateStep1()) {
            toggleTab(2);
        } else if (activeTab === 2 && validateStep2()) {
            toggleTab(3);
        }
    };

    const handlePrevious = () => {
        if (activeTab > 1) {
            toggleTab(activeTab - 1);
        }
    };

    // Form handlers
    const handleInputChange = (field: keyof EndpointFormData, value: string) => {
        let processedValue: any = value;

        if (field === 'endpointSlug') {
            // Convert to lowercase and validate characters as user types
            processedValue = value.toLowerCase().replace(/[^a-z0-9-]/g, '').substring(0, 50);
        } else if (field === 'status' || field === 'sourceType') {
            // Convert status and sourceType to number
            processedValue = parseInt(value);
        }

        setFormData(prev => ({ ...prev, [field]: processedValue }));

        // Clear error when user starts typing (only for fields that can have errors)
        if (field !== 'sampleFile' && errors[field as keyof FormErrors]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    // File upload handler
    const onDrop = (acceptedFiles: File[]) => {
        if (acceptedFiles.length > 0) {
            setFormData(prev => ({ ...prev, sampleFile: acceptedFiles[0] }));
        }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'text/*': ['.txt', '.csv', '.json'],
            'application/json': ['.json'],
            'application/csv': ['.csv']
        },
        maxFiles: 1
    });

    // Image paste handler
    const handleImagePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
        const items = e.clipboardData?.items;
        if (!items) return;

        for (let i = 0; i < items.length; i++) {
            const item = items[i];

            // Check if the item is an image
            if (item.type.indexOf('image') !== -1) {
                const blob = item.getAsFile();
                if (blob) {
                    // Convert blob to base64
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        const base64data = reader.result as string;
                        setFormData(prev => ({ ...prev, sampleImage: base64data }));
                        console.log('Image pasted successfully');
                    };
                    reader.readAsDataURL(blob);
                }
                e.preventDefault();
                break;
            }
        }
    };

    // Clear pasted image
    const handleClearImage = () => {
        setFormData(prev => ({ ...prev, sampleImage: '' }));
    };

    // Image file upload handler
    const handleImageFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Verify it's an image
        if (!file.type.startsWith('image/')) {
            alert('Please select an image file');
            return;
        }

        // Convert file to base64
        const reader = new FileReader();
        reader.onloadend = () => {
            const base64data = reader.result as string;
            setFormData(prev => ({ ...prev, sampleImage: base64data }));
            console.log('Image file uploaded successfully');
        };
        reader.readAsDataURL(file);
    };

    // Execute button handler
    const handleExecute = async () => {
        setIsExecuting(true);
        setExecutionOutput('Processing...');

        try {
            // Prepare sample document based on sourceType
            let documentData = '';

            if (formData.sourceType === 1) {
                // File type - read and encode the file
                if (!formData.sampleFile) {
                    setExecutionOutput('Error: Please upload a sample file first.');
                    setIsExecuting(false);
                    return;
                }

                // Read file and convert to base64
                const fileToRead = formData.sampleFile;
                const fileReader = new FileReader();
                documentData = await new Promise<string>((resolve, reject) => {
                    fileReader.onload = () => {
                        const base64 = fileReader.result as string;
                        // Remove data:mime/type;base64, prefix if present
                        const base64Data = base64.includes(',') ? base64.split(',')[1] : base64;
                        resolve(base64Data);
                    };
                    fileReader.onerror = reject;
                    fileReader.readAsDataURL(fileToRead);
                });
            } else if (formData.sourceType === 2) {
                // Text/HTML type
                if (!formData.sampleText || !formData.sampleText.trim()) {
                    setExecutionOutput('Error: Please enter sample text first.');
                    setIsExecuting(false);
                    return;
                }

                // Encode text to base64
                documentData = btoa(unescape(encodeURIComponent(formData.sampleText)));
            } else {
                // Image type (sourceType = 3)
                if (!formData.sampleImage || !formData.sampleImage.trim()) {
                    setExecutionOutput('Error: Please paste an image first.');
                    setIsExecuting(false);
                    return;
                }

                // Extract base64 data (remove data:image/...;base64, prefix if present)
                documentData = formData.sampleImage.includes(',')
                    ? formData.sampleImage.split(',')[1]
                    : formData.sampleImage;
            }

            // Call the test API with the document and prompt
            const apiClient = new APIClient();
            const testPayload = {
                document: documentData,
                prompt: formData.prompt,
                jsonFormat: formData.jsonFormatData,
                sourceType: formData.sourceType
            };

            // Call portal-api test endpoint which will call ai-agent-ts
            const response = await apiClient.create('/docprocessor/test', testPayload);

            // Display the result
            const formattedOutput = `Execution completed at ${new Date().toLocaleTimeString()}\n\n` +
                `Result:\n${JSON.stringify(response, null, 2)}`;
            setExecutionOutput(formattedOutput);

        } catch (error: any) {
            console.error('Execution error:', error);
            const errorMessage = `Error: ${error.message || 'Failed to execute. Please try again.'}\n\n` +
                `Details: ${error.response?.data?.error || error.toString()}`;
            setExecutionOutput(errorMessage);
        } finally {
            setIsExecuting(false);
        }
    };

    // Save endpoint
    const handleSave = async () => {
        try {
            const apiClient = new APIClient();
            const requestBody = {
                title: formData.title,
                description: formData.description,
                prompt: formData.prompt,
                jsonFormatData: formData.jsonFormatData,
                endpointSlug: formData.endpointSlug,
                status: formData.status,
                sourceType: formData.sourceType
            };

            if (isEditMode && editData) {
                // Update existing endpoint - PUT /docprocessor/{processorId}
                console.log('Updating endpoint - editData:', editData);
                console.log('All editData keys:', Object.keys(editData));

                // Use processorId from API response
                const processorId = editData.processorId || editData.id || editData._id || editData.uuid;
                console.log('Processor ID found:', processorId);
                console.log('Request body:', requestBody);

                if (!processorId) {
                    console.error('No valid processor ID found. Available fields:', Object.keys(editData));
                    throw new Error(`Processor ID is missing for update operation. Available fields: ${Object.keys(editData).join(', ')}`);
                }

                await apiClient.put(`/docprocessor/${processorId}`, requestBody);
            } else {
                // Create new endpoint - POST /docprocessor/{projectId}/{envId}
                console.log('Creating endpoint:', requestBody);
                console.log('Environment ID:', envId);
                await apiClient.create(`/docprocessor/${projectId}/${envId}`, requestBody);

                // Track endpoint creation success (only for new endpoints, not edits)
                trackEndpointSubmit(
                    formData.endpointSlug || 'unknown',
                    formData.title
                );
            }

            // Close the modal
            handleToggle();
        } catch (error) {
            console.error('Error saving endpoint:', error);
            // You can add toast notification here for error handling
        }
    };

    const renderStepContent = () => {
        return (
            <TabContent activeTab={activeTab}>
                <TabPane tabId={1}>
                    <div>
                        <Form>
                            <FormGroup>
                                <Label for="title">Title <span className="text-danger">*</span></Label>
                                <Input
                                    type="text"
                                    id="title"
                                    value={formData.title}
                                    onChange={(e) => handleInputChange('title', e.target.value)}
                                    invalid={!!errors.title}
                                />
                                {errors.title && <FormFeedback>{errors.title}</FormFeedback>}
                            </FormGroup>
                            
                            <FormGroup>
                                <Label for="description">Description <span className="text-danger">*</span></Label>
                                <Input
                                    type="textarea"
                                    id="description"
                                    rows="3"
                                    value={formData.description}
                                    onChange={(e) => handleInputChange('description', e.target.value)}
                                    invalid={!!errors.description}
                                />
                                {errors.description && <FormFeedback>{errors.description}</FormFeedback>}
                            </FormGroup>

                            <Row>
                                <Col md={4}>
                                    <FormGroup>
                                        <Label for="sourceType">
                                            Source Data Type{' '}
                                            <i
                                                className="ri-information-line text-muted"
                                                id="sourceTypeTooltip"
                                                style={{ cursor: 'help' }}
                                            />
                                        </Label>
                                        <UncontrolledTooltip placement="top" target="sourceTypeTooltip">
                                            Choose the type of input your endpoint will accept:<br/>
                                            • <strong>PDF/Text File</strong> - For documents and text files<br/>
                                            • <strong>Text/HTML</strong> - For plain text or HTML content<br/>
                                            • <strong>Image</strong> - For screenshots or scanned documents
                                        </UncontrolledTooltip>
                                        <Input
                                            type="select"
                                            id="sourceType"
                                            value={formData.sourceType}
                                            onChange={(e) => handleInputChange('sourceType', e.target.value)}
                                        >
                                            <option value={1}>PDF/Text File</option>
                                            <option value={2}>Text/Html</option>
                                            <option value={3}>Image</option>
                                        </Input>
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup>
                                        <Label for="endpointSlug">
                                            Endpoint Slug <span className="text-danger">*</span>{' '}
                                            <i
                                                className="ri-information-line text-muted"
                                                id="endpointSlugTooltip"
                                                style={{ cursor: 'help' }}
                                            />
                                        </Label>
                                        <UncontrolledTooltip placement="top" target="endpointSlugTooltip">
                                            URL-safe name for your API endpoint. Use lowercase letters, numbers, and hyphens only.<br/>
                                            <strong>Example:</strong> <code>invoice-parser</code> becomes <code>/v1/flow/invoice-parser</code>
                                        </UncontrolledTooltip>
                                        <Input
                                            type="text"
                                            id="endpointSlug"
                                            value={formData.endpointSlug}
                                            onChange={(e) => handleInputChange('endpointSlug', e.target.value)}
                                            invalid={!!errors.endpointSlug}
                                            placeholder="e.g. invoice-parser"
                                        />
                                        <small className="text-muted">Lowercase letters, numbers, and dashes only</small>
                                        {errors.endpointSlug && <FormFeedback>{errors.endpointSlug}</FormFeedback>}
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup>
                                        <Label>API Endpoint</Label>
                                        <Input
                                            type="text"
                                            value={formData.endpointSlug ? `${getBaseUrl()}/v1/flow/${formData.endpointSlug}` : `${getBaseUrl()}/v1/flow/{endpointSlug}`}
                                            readOnly
                                            className="text-muted"
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </Form>
                    </div>
                </TabPane>

                <TabPane tabId={2}>
                    <div>
                        <Row>
                            <Col md={6}>
                                <Card className="h-100">
                                    <CardHeader>
                                        <h6 className="mb-0">Input</h6>
                                    </CardHeader>
                                    <CardBody>
                                        {/* Conditionally render based on sourceType */}
                                        {formData.sourceType === 1 ? (
                                            // File Upload for sourceType = 1
                                            <FormGroup className="mb-3">
                                                <Label>
                                                    Sample File{' '}
                                                    <i
                                                        className="ri-information-line text-muted"
                                                        id="sampleFileTooltip"
                                                        style={{ cursor: 'help' }}
                                                    />
                                                </Label>
                                                <UncontrolledTooltip placement="top" target="sampleFileTooltip">
                                                    Upload a representative document to test your AI configuration before saving the endpoint.<br/>
                                                    This helps ensure your prompt and JSON format produce the expected results.
                                                </UncontrolledTooltip>
                                                <div
                                                    {...getRootProps()}
                                                    className={`border border-dashed rounded p-3 text-center cursor-pointer ${isDragActive ? 'border-primary bg-light' : 'border-secondary'}`}
                                                    style={{ minHeight: '80px', cursor: 'pointer' }}
                                                >
                                                    <input {...getInputProps()} />
                                                    {formData.sampleFile ? (
                                                        <div>
                                                            <i className="ri-file-text-line text-primary fs-24"></i>
                                                            <p className="mb-0 mt-2">{formData.sampleFile.name}</p>
                                                        </div>
                                                    ) : (
                                                        <div>
                                                            <i className="ri-upload-cloud-2-line text-muted fs-24"></i>
                                                            <p className="mb-0 mt-2 text-muted">
                                                                {isDragActive ? 'Drop file here...' : 'Drag & drop a file here, or click to select'}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </FormGroup>
                                        ) : formData.sourceType === 2 ? (
                                            // Rich Text Editor for sourceType = 2
                                            <FormGroup className="mb-3">
                                                <Label>
                                                    Sample Text{' '}
                                                    <i
                                                        className="ri-information-line text-muted"
                                                        id="sampleTextTooltip"
                                                        style={{ cursor: 'help' }}
                                                    />
                                                </Label>
                                                <UncontrolledTooltip placement="top" target="sampleTextTooltip">
                                                    Paste sample text or HTML content to test your AI configuration.<br/>
                                                    Use representative data that matches what your API will receive.
                                                </UncontrolledTooltip>
                                                <div style={{ border: '1px solid #ced4da', borderRadius: '0.25rem' }}>
                                                    <CKEditor
                                                        editor={ClassicEditor}
                                                        data={formData.sampleText}
                                                        onChange={(event, editor) => {
                                                            const data = editor.getData();
                                                            setFormData(prev => ({ ...prev, sampleText: data }));
                                                        }}
                                                        config={{
                                                            licenseKey: 'GPL',
                                                            placeholder: 'Enter or paste your text here...',
                                                            toolbar: [
                                                                'heading', '|',
                                                                'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|',
                                                                'blockQuote', 'insertTable', '|',
                                                                'undo', 'redo'
                                                            ]
                                                        }}
                                                    />
                                                </div>
                                                <small className="text-muted">Copy and paste your text content here. Basic HTML formatting is supported.</small>
                                            </FormGroup>
                                        ) : (
                                            // Image upload/paste area for sourceType = 3
                                            <FormGroup className="mb-3">
                                                <Label>Sample Image</Label>

                                                {!formData.sampleImage ? (
                                                    <>
                                                        {/* File upload option */}
                                                        <div className="mb-3">
                                                            <Input
                                                                type="file"
                                                                accept="image/*"
                                                                onChange={handleImageFileUpload}
                                                                id="imageFileUpload"
                                                            />
                                                        </div>

                                                        {/* Divider */}
                                                        <div className="text-center my-3">
                                                            <span className="text-muted">OR</span>
                                                        </div>

                                                        {/* Paste option */}
                                                        <div
                                                            onPaste={handleImagePaste}
                                                            style={{
                                                                border: '2px dashed #ced4da',
                                                                borderRadius: '0.25rem',
                                                                padding: '20px',
                                                                minHeight: '150px',
                                                                cursor: 'pointer',
                                                                textAlign: 'center',
                                                                backgroundColor: '#f8f9fa'
                                                            }}
                                                            tabIndex={0}
                                                        >
                                                            <i className="ri-image-add-line text-muted fs-24"></i>
                                                            <p className="mb-0 mt-2 text-muted">
                                                                Click here and press Ctrl+V (or Cmd+V) to paste an image from clipboard
                                                            </p>
                                                        </div>
                                                        <small className="text-muted">
                                                            Upload an image file or copy an image and paste it using Ctrl+V or Cmd+V
                                                        </small>
                                                    </>
                                                ) : (
                                                    // Image preview
                                                    <div
                                                        style={{
                                                            border: '2px solid #ced4da',
                                                            borderRadius: '0.25rem',
                                                            padding: '20px',
                                                            textAlign: 'center'
                                                        }}
                                                    >
                                                        <img
                                                            src={formData.sampleImage}
                                                            alt="Uploaded/Pasted"
                                                            style={{ maxWidth: '100%', maxHeight: '300px' }}
                                                        />
                                                        <div className="mt-2">
                                                            <Button color="danger" size="sm" onClick={handleClearImage}>
                                                                Clear Image
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                            </FormGroup>
                                        )}

                                        <FormGroup className="mb-3">
                                            <Label for="prompt">
                                                Prompt <span className="text-danger">*</span>{' '}
                                                <i
                                                    className="ri-information-line text-muted"
                                                    id="promptTooltip"
                                                    style={{ cursor: 'help' }}
                                                />
                                            </Label>
                                            <UncontrolledTooltip placement="top" target="promptTooltip">
                                                Be specific: describe exactly what data to extract and in what format.<br/>
                                                <strong>Example:</strong> "Extract invoice number, date (YYYY-MM-DD format), vendor name, total amount, and itemized list with descriptions, quantities, and prices"
                                            </UncontrolledTooltip>
                                            <Input
                                                type="textarea"
                                                id="prompt"
                                                rows="3"
                                                value={formData.prompt}
                                                onChange={(e) => handleInputChange('prompt', e.target.value)}
                                                invalid={!!errors.prompt}
                                                placeholder="Enter your processing prompt here..."
                                            />
                                            {errors.prompt && <FormFeedback>{errors.prompt}</FormFeedback>}
                                        </FormGroup>

                                        <FormGroup className="mb-3">
                                            <Label for="expectedJson">
                                                Expected JSON Format <span className="text-danger">*</span>{' '}
                                                <i
                                                    className="ri-information-line text-muted"
                                                    id="expectedJsonTooltip"
                                                    style={{ cursor: 'help' }}
                                                />
                                            </Label>
                                            <UncontrolledTooltip placement="top" target="expectedJsonTooltip">
                                                Define the structure of the output data. Use field names your application expects.<br/>
                                                Include data types as string values (e.g., "string", "number", "YYYY-MM-DD").<br/>
                                                <strong>Tip:</strong> Use meaningful field names that match your database schema.
                                            </UncontrolledTooltip>
                                            <Input
                                                type="textarea"
                                                id="expectedJson"
                                                rows="5"
                                                value={formData.jsonFormatData}
                                                onChange={(e) => handleInputChange('jsonFormatData', e.target.value)}
                                                invalid={!!errors.jsonFormatData}
                                                placeholder={'{\n  "example": "Expected output structure"\n}'}
                                            />
                                            {errors.jsonFormatData && <FormFeedback>{errors.jsonFormatData}</FormFeedback>}
                                        </FormGroup>

                                        <Button
                                            color="primary"
                                            onClick={handleExecute}
                                            disabled={isExecuting || !formData.prompt.trim() || !formData.jsonFormatData.trim()}
                                        >
                                            {isExecuting ? 'Executing...' : 'Execute'}
                                        </Button>
                                    </CardBody>
                                </Card>
                            </Col>

                            <Col md={6}>
                                <Card className="h-100">
                                    <CardHeader>
                                        <h6 className="mb-0">Output</h6>
                                    </CardHeader>
                                    <CardBody>
                                        <Input
                                            type="textarea"
                                            value={executionOutput}
                                            readOnly
                                            style={{
                                                height: '100%',
                                                minHeight: '280px',
                                                fontFamily: 'monospace',
                                                fontSize: '12px'
                                            }}
                                            placeholder="Execution output will appear here..."
                                        />
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </div>
                </TabPane>

                <TabPane tabId={3}>
                    <div>
                        <Form>
                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Title</Label>
                                        <Input
                                            type="text"
                                            value={formData.title}
                                            readOnly
                                            style={{ backgroundColor: '#e9ecef' }}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>API Endpoint</Label>
                                        <Input
                                            type="text"
                                            value={`${getBaseUrl()}/v1/flow/${formData.endpointSlug}`}
                                            readOnly
                                            style={{ backgroundColor: '#e9ecef' }}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Source Data Type</Label>
                                        <Input
                                            type="text"
                                            value={
                                                formData.sourceType === 1 ? 'PDF/Text File' :
                                                formData.sourceType === 2 ? 'Text/Html' :
                                                formData.sourceType === 3 ? 'Image' : 'Unknown'
                                            }
                                            readOnly
                                            style={{ backgroundColor: '#e9ecef' }}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>
                                            Status{' '}
                                            <i
                                                className="ri-information-line text-muted"
                                                id="statusTooltip"
                                                style={{ cursor: 'help' }}
                                            />
                                        </Label>
                                        <UncontrolledTooltip placement="top" target="statusTooltip">
                                            <strong>Active:</strong> Endpoint will accept API calls immediately<br/>
                                            <strong>Inactive:</strong> Endpoint won't accept API calls (useful for testing before activating)
                                        </UncontrolledTooltip>
                                        <Input
                                            type="select"
                                            value={formData.status}
                                            onChange={(e) => handleInputChange('status', e.target.value)}
                                        >
                                            <option value={1}>Active</option>
                                            <option value={0}>Inactive</option>
                                        </Input>
                                    </FormGroup>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={12}>
                                    <FormGroup>
                                        <Label>Description</Label>
                                        <Input
                                            type="textarea"
                                            rows="3"
                                            value={formData.description}
                                            readOnly
                                            style={{ backgroundColor: '#e9ecef' }}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Prompt</Label>
                                        <Input
                                            type="textarea"
                                            rows="8"
                                            value={formData.prompt}
                                            readOnly
                                            style={{ fontSize: '14px', backgroundColor: '#e9ecef' }}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Expected JSON Format</Label>
                                        <Input
                                            type="textarea"
                                            rows="8"
                                            value={formData.jsonFormatData}
                                            readOnly
                                            style={{ fontFamily: 'monospace', fontSize: '12px', backgroundColor: '#e9ecef' }}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={12}>
                                    <FormGroup>
                                        <Label>cURL Command Example</Label>
                                        <Input
                                            type="textarea"
                                            rows="10"
                                            value={(() => {
                                                const apiUrl = `${process.env.REACT_APP_INTEGR_API_URL || 'https://api.integr.dev'}/v1/flow/${formData.endpointSlug}`;

                                                if (formData.sourceType === 1) {
                                                    // File upload example
                                                    return `# Upload a file (multipart/form-data)
curl -X POST "${apiUrl}" \\
  -H "Authorization: Bearer YOUR_API_TOKEN" \\
  -F "file=@/path/to/your/file.pdf"

# Or send base64-encoded file (application/json)
curl -X POST "${apiUrl}" \\
  -H "Authorization: Bearer YOUR_API_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "file": "BASE64_ENCODED_FILE_CONTENT"
  }'`;
                                                } else if (formData.sourceType === 2) {
                                                    // Text/HTML example
                                                    return `# Send text/HTML content (base64-encoded)
curl -X POST "${apiUrl}" \\
  -H "Authorization: Bearer YOUR_API_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "file": "BASE64_ENCODED_TEXT_CONTENT"
  }'

# Example: Encode text to base64
# echo "Your text content here" | base64`;
                                                } else if (formData.sourceType === 3) {
                                                    // Image example
                                                    return `# Send image (base64-encoded)
curl -X POST "${apiUrl}" \\
  -H "Authorization: Bearer YOUR_API_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "file": "BASE64_ENCODED_IMAGE_CONTENT"
  }'

# Example: Encode image to base64
# base64 /path/to/your/image.png | tr -d '\\n'`;
                                                } else {
                                                    return '# Select a source type to see the cURL command example';
                                                }
                                            })()}
                                            readOnly
                                            style={{ fontFamily: 'monospace', fontSize: '11px', backgroundColor: '#e9ecef' }}
                                        />
                                        <small className="text-muted">
                                            Replace YOUR_API_TOKEN with your authentication token
                                        </small>
                                    </FormGroup>
                                </Col>
                            </Row>
                        </Form>
                    </div>
                </TabPane>
            </TabContent>
        );
    };


    return (
        <>
            <Modal isOpen={isOpen} toggle={handleToggle} size="xl" centered>
                <ModalHeader toggle={handleToggle}>
                    <div className="d-flex align-items-center justify-content-between w-100">
                        <span>{isEditMode ? 'Edit Endpoint' : 'Create New Endpoint'}</span>
                        <Button
                            color="info"
                            size="sm"
                            outline
                            onClick={() => setIsHelpModalOpen(true)}
                            className="me-4"
                        >
                            <i className="ri-question-line me-1"></i>
                            Help Guide
                        </Button>
                    </div>
                </ModalHeader>
            
            <ModalBody>
                <Form className="form-steps">
                    {/* Arrow Nav Steps */}
                    <div className="step-arrow-nav mb-4">
                        <Nav className="nav-pills custom-nav nav-justified" role="tablist">
                            <NavItem>
                                <NavLink
                                    href="#"
                                    className={classnames({
                                        active: activeTab === 1,
                                        done: activeTab <= 3 && activeTab > 1,
                                    })}
                                    onClick={() => {
                                        toggleTab(1);
                                    }}
                                >
                                    Basic Info
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    href="#"
                                    className={classnames({
                                        active: activeTab === 2,
                                        done: activeTab <= 3 && activeTab > 2,
                                    })}
                                    onClick={() => {
                                        toggleTab(2);
                                    }}
                                >
                                    Playground
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    href="#"
                                    className={classnames({
                                        active: activeTab === 3,
                                        done: false,
                                    })}
                                    onClick={() => {
                                        toggleTab(3);
                                    }}
                                >
                                    Confirm
                                </NavLink>
                            </NavItem>
                        </Nav>
                    </div>

                    {/* Step Content */}
                    <div style={{ backgroundColor: 'var(--vz-body-bg)', color: 'var(--vz-body-color)', padding: '1.5rem', borderRadius: '0.375rem' }}>
                        {renderStepContent()}
                    </div>
                </Form>
            </ModalBody>
            
            <ModalFooter className="justify-content-between">
                <div>
                    {activeTab > 1 && (
                        <Button color="light" onClick={handlePrevious} className="btn-label">
                            <i className="ri-arrow-left-line label-icon align-middle fs-16 me-2"></i>
                            Previous
                        </Button>
                    )}
                </div>
                
                <div className="d-flex gap-2">
                    <Button color="light" onClick={handleToggle}>
                        Cancel
                    </Button>
                    
                    {activeTab < 3 ? (
                        <Button color="success" onClick={handleNext} className="btn-label right">
                            <i className="ri-arrow-right-line label-icon align-middle fs-16 ms-2"></i>
                            Next
                        </Button>
                    ) : (
                        <Button color="primary" onClick={handleSave}>
                            <i className="ri-save-line align-middle me-1"></i>
                            {isEditMode ? 'Update Endpoint' : 'Save Endpoint'}
                        </Button>
                    )}
                </div>
            </ModalFooter>
        </Modal>

        {/* Help Modal */}
        <EndpointHelpModal
            isOpen={isHelpModalOpen}
            toggle={() => setIsHelpModalOpen(false)}
        />
        </>
    );
};

export default CreateEndpointModal;